import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TicketListModal from '../components/TicketListModal';
import PeriodFilter, { PERIODS, withinPeriod } from '../components/PeriodFilter';
import { hours as hrs } from '../lib/hours';
import { moneyRound } from '../lib/money';
import { fmtDate } from '../lib/when';
import './Dashboard.css';
import RequesterName from '../components/RequesterName';
import {
  PieChart, Pie, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const share = (n, total) => (total > 0 ? Math.round((n / total) * 100) : 0);

// Module scope: re-declaring this inside the component remounted all four
// tiles on every poll. The coloured progress bar is gone - it divided by
// stats.totalTickets, so it rendered NaN% whenever the board was empty.
// Chart fills come from the theme tokens so the charts follow light and dark
// with everything else, instead of being frozen in one palette.
const CHART_COLORS = {
  Open: 'var(--color-critical-solid)',
  'In Progress': 'var(--color-info-solid)',
  'On Hold': 'var(--color-warning-solid)',
  Resolved: 'var(--color-success-solid)',
  Closed: 'var(--color-idle-solid)',
  'Awaiting Approval': 'var(--color-primary)',
  Low: 'var(--color-info-solid)',
  Medium: 'var(--color-warning-solid)',
  High: 'var(--color-critical-solid)',
  Critical: 'var(--color-critical)'
};
const FALLBACK_COLOR = 'var(--color-idle-solid)';

const tally = (rows, pick, fallback) => {
  const counts = {};
  rows.forEach((r) => {
    const key = pick(r) || fallback;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

function deriveDashboard(tickets) {
  const statusCounts = tally(tickets, (t) => t.status, 'Unknown');
  const priorityCounts = tally(tickets, (t) => t.priority, 'Unknown');
  const deptCounts = tally(tickets, (t) => t.department, 'Unassigned');
  const catCounts = tally(tickets, (t) => t.category, 'Other');

  return {
    stats: {
      totalTickets: tickets.length,
      openTickets: tickets.filter((t) => t.status === 'Open').length,
      inProgressTickets: tickets.filter((t) => t.status === 'In Progress').length,
      resolvedTickets: tickets.filter((t) => t.status === 'Resolved').length
    },
    chartData: {
      statusDistribution: Object.entries(statusCounts)
        .map(([name, value]) => ({ name, value, fill: CHART_COLORS[name] || FALLBACK_COLOR })),
      priorityDistribution: Object.entries(priorityCounts)
        .map(([name, value]) => ({ name, value, fill: CHART_COLORS[name] || FALLBACK_COLOR })),
      departmentBreakdown: Object.entries(deptCounts)
        .sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
      categoryBreakdown: Object.entries(catCounts)
        .sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })),
      // slice() first: sort() mutates, and sorting the caller's array in place
      // reorders the very list the modals are built from.
      recentTickets: tickets.slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5)
    }
  };
}

function StatCard({ title, value, tone, share, meta, period, onOpen }) {
  const body = (
    <>
      <div className="stat-tile-label">
        {title}
        {/* The hours tiles say which period they are summing. The ticket
            tiles beside them follow the same filter, but a count of tickets
            reads as "right now" either way while "74h billed" with no period
            reads as a lifetime figure - and then changes when the filter
            does, which looked like the tile being wrong. */}
        {period && <span className="stat-tile-period">{period}</span>}
      </div>
      <div className={tone ? `stat-tile-value tone-${tone}` : 'stat-tile-value'}>{value}</div>
      {typeof share === 'number' && (
        <div className="stat-tile-meta">{share}% of all tickets</div>
      )}
      {/* title, because at eight tiles across the meta is clipped to one line
          and the full sentence should still be reachable. */}
      {meta && <div className="stat-tile-meta" title={meta}>{meta}</div>}
    </>
  );
  // A real <button> when it opens something, so it is keyboard reachable and
  // announced as actionable rather than being a div with a click handler.
  if (!onOpen) return <div className="stat-tile">{body}</div>;
  return (
    <button type="button" className="stat-tile is-clickable" onClick={onOpen}
            title={`Show the tickets behind ${title.toLowerCase()}`}>
      {body}
    </button>
  );
}

/**
 * One row of a breakdown list: a label, its count, and a bar.
 *
 * A real <button>, not a div with a click handler, so it is reachable by
 * keyboard and announced as something that does something. The percentage is
 * guarded because an empty period divides by zero, and `width: NaN%` is
 * silently dropped - the bar then renders at its natural width, which is full,
 * so every row claims 100% on exactly the screen that has no tickets on it.
 */
function BarRow({ name, count, total, tone, onOpen }) {
  const pct = total > 0 ? Math.min(100, (count / total) * 100) : 0;
  return (
    <button
      type="button"
      className="dash-bar-row is-clickable"
      onClick={onOpen}
      title={`Show the ${count === 1 ? 'ticket' : 'tickets'} for ${name}`}
    >
      <div className="dash-bar-head">
        <span className="dash-bar-name">{name}</span>
        <span className="dash-bar-count">{count}</span>
      </div>
      <div className="dash-bar-track">
        <div className={tone ? `dash-bar-fill tone-${tone}` : 'dash-bar-fill'}
             style={{ width: `${pct}%` }} />
      </div>
    </button>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const [stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0
  });
  const [chartData, setChartData] = useState({
    statusDistribution: [],
    priorityDistribution: [],
    departmentBreakdown: [],
    categoryBreakdown: [],
    recentTickets: []
  });
  const [loading, setLoading] = useState(true);
  // The raw rows are kept so changing the period re-derives everything in the
  // browser instead of going back to the API.
  const [allTickets, setAllTickets] = useState([]);
  // Said rather than swallowed: a failed load used to render "0 open, 0
  // overdue" and an empty chart with no sign the API had failed - while the
  // hours tiles on the same page did say so.
  const [loadError, setLoadError] = useState(null);
  // The server's "all" stops at a ceiling (lib/ticket-list.js) and says so;
  // the page then says which part it is counting.
  const [capped, setCapped] = useState(null);   // { shown, total } or null
  const [period, setPeriod] = useState('30');
  const [drill, setDrill] = useState(null);
  // Hours are the one thing the dashboard could not derive from the ticket
  // rows: they live in hours_logs, ticket_approvals and billing_history, and
  // summing three tables in the browser would mean fetching all three.
  const [hoursSummary, setHoursSummary] = useState(null);

  // Fetching and deriving are separate now: the raw rows are fetched once, and
  // every number on the page is derived from the period-filtered subset. That
  // is what lets the period buttons work without another round trip.
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      // /api/tickets pages at 20 by default, so the dashboard was counting at
      // most 20 tickets whatever the real total - then 500. It asks for the
      // envelope with size=all now (routes/tickets-core.js), which the
      // server caps and says so (`capped`), and the page says it in turn
      // rather than presenting a part as the whole. Past that ceiling these
      // counts belong in a SQL aggregate rather than a browser tally. A bare
      // array back is an older server: counted as it comes.
      const response = await axios.get(`${API_URL}/tickets`, { params: { envelope: 1, size: 'all', sort: 'created_at', dir: 'desc' } });
      if (Array.isArray(response.data)) {
        setAllTickets(response.data);
        setCapped(null);
      } else {
        const rows = response.data?.tickets ?? [];
        setAllTickets(rows);
        setCapped(response.data?.capped ? { shown: rows.length, total: response.data.total } : null);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setAllTickets([]);
      setLoadError(error.response?.data?.error
        || (error.response
          ? `Could not load the tickets (server returned ${error.response.status}).`
          : 'Could not reach the server to load the tickets.'));
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Follows the period filter like everything else on the page, so the tiles
  // are answering the same question as the tickets beside them.
  useEffect(() => {
    let live = true;
    // PERIODS carries days: null for "All time", which the endpoint reads as
    // no cutoff, so the lookup is explicit rather than a ?? that would miss it.
    const found = PERIODS.find((p) => p.key === period);
    const days = found ? found.days : null;
    axios.get(`${API_URL}/analytics/hours-summary`,
      { params: days ? { days } : {} })
      .then((r) => { if (live) setHoursSummary(r.data || { error: true }); })
      // The tiles stay on the page and say they could not load. They used
      // to be dropped altogether on an error, and four tiles that silently
      // disappear are indistinguishable from four tiles that were never
      // built - which is a bug report, not a diagnosis.
      .catch(() => { if (live) setHoursSummary({ error: true }); });
    return () => { live = false; };
  }, [API_URL, period]);

  // Everything visible is derived from the period-filtered rows, so changing
  // the period re-renders without another request.
  const periodTickets = useMemo(
    () => withinPeriod(allTickets, period), [allTickets, period]);

  useEffect(() => {
    const derived = deriveDashboard(periodTickets);
    setStats(derived.stats);
    setChartData(derived.chartData);
  }, [periodTickets]);

  const periodLabel = (PERIODS.find((p) => p.key === period) || {}).label || '';
  // "last 30 days", or "all time" - which is not a thing one can have the
  // last of, and read "last all time" on every popup opened with All chosen.
  const periodPhrase = (label) =>
    (/^all/i.test(label) ? 'all time' : `last ${label.toLowerCase()}`);

  const openDrill = (title, rows) =>
    setDrill({ title, subtitle: periodPhrase(periodLabel), tickets: rows });

  /*
   * The four hours tiles, which cannot drill down in the browser.
   *
   * Gordon: "can you make hours logged, hours billed, ready to bill and
   * awaiting sign off be interactive like the rest of the boxes (ie popup
   * making up the tickets which make up the box)."
   *
   * The other eight filter `periodTickets`, a list this page already holds.
   * These four are sums over hours_logs, ticket_approvals and
   * billing_history - none of which the dashboard loads, and none of which a
   * ticket row carries - so the list has to be fetched, and the endpoint
   * builds it from the same WHERE clause as the number so the two agree.
   *
   * It opens immediately with the figure as its subtitle rather than waiting
   * on the request: a tile that does nothing for half a second reads as a
   * tile that does nothing, which is what these four did until today.
   */
  const openHoursDrill = async (kind, title, figure) => {
    const found = PERIODS.find((p) => p.key === period);
    const days = found ? found.days : null;
    const subtitle = `${figure} · ${periodPhrase(periodLabel)}`;
    setDrill({ title, subtitle, tickets: [], loading: true });
    try {
      const r = await axios.get(`${API_URL}/analytics/hours-summary/tickets`,
        { params: days ? { kind, days } : { kind } });
      setDrill({ title, subtitle, tickets: Array.isArray(r.data) ? r.data : [] });
    } catch {
      // The modal stays open and says it found nothing rather than vanishing.
      // A popup that closes itself on an error is indistinguishable from a
      // button that did not work.
      setDrill({ title, subtitle, tickets: [], error: true });
    }
  };

  const byStatus = (name) => periodTickets.filter((t) => (t.status || 'Unknown') === name);
  const byPriority = (name) => periodTickets.filter((t) => (t.priority || 'Unknown') === name);
  // The same fallbacks deriveDashboard counts with, deliberately: a drill-down
  // that does not reproduce its own number is worse than no drill-down. A
  // ticket with no category is counted under "Other" up there, so it has to
  // appear under "Other" in here too, even though Other is also a real
  // category - splitting them would make the list disagree with the bar.
  const byDepartment = (name) =>
    periodTickets.filter((t) => (t.department || 'Unassigned') === name);
  const byCategory = (name) =>
    periodTickets.filter((t) => (t.category || 'Other') === name);


  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-head">
        <h1 style={{ margin: 0, color: 'var(--color-text-primary)' }}>Dashboard</h1>
        <PeriodFilter value={period} onChange={setPeriod} label="Dashboard period" />
      </div>

      {/* Every figure below is derived from the rows this asked for; when
          that request failed the figures are not zero, they are unknown. */}
      {loadError && (
        <p className="dash-problem" role="alert"
           style={{ color: 'var(--color-danger)', margin: '0 0 1rem' }}>
          {loadError} The ticket counts and charts below are empty because of it.{' '}
          <button type="button" className="btn btn-secondary btn-small" onClick={fetchDashboardData}>Try again</button>
        </p>
      )}
      {capped && !loadError && (
        <p className="dash-capped" role="status"
           style={{ color: 'var(--color-text-secondary)', margin: '0 0 1rem' }}>
          Counting the first {capped.shown} of {capped.total} tickets, newest first; the rest are not in these figures.
        </p>
      )}

      {/* One row, eight tiles: four counting tickets and four counting hours.
          They were two rows of four, which put the money figures below the
          fold on a laptop - and they are the half of this page that says what
          the work costs. The tiles are denser to pay for it: the meta lines
          are clipped to a few words each, because at this width a sentence
          wraps to three lines and pushes the row taller than the two it
          replaced. */}
      <div className="stat-grid is-eight">
        {/* The ticket tiles carry the period too, so the row's labels line
            up and nobody has to wonder whether "Open" means open now or
            opened in the period (it is the latter - withinPeriod filters on
            created_at). */}
        <StatCard title="Total tickets" value={stats.totalTickets}
                  period={periodPhrase(periodLabel)}
                  onOpen={() => openDrill('All tickets', periodTickets)} />
        <StatCard title="Open" value={stats.openTickets} tone="critical"
                  period={periodPhrase(periodLabel)}
                  share={share(stats.openTickets, stats.totalTickets)}
                  onOpen={() => openDrill('Open tickets', byStatus('Open'))} />
        <StatCard title="In progress" value={stats.inProgressTickets} tone="info"
                  period={periodPhrase(periodLabel)}
                  share={share(stats.inProgressTickets, stats.totalTickets)}
                  onOpen={() => openDrill('In progress', byStatus('In Progress'))} />
        <StatCard title="Resolved" value={stats.resolvedTickets} tone="success"
                  period={periodPhrase(periodLabel)}
                  share={share(stats.resolvedTickets, stats.totalTickets)}
                  onOpen={() => openDrill('Resolved tickets', byStatus('Resolved'))} />

        {/* What the work costs, which the ticket counts beside them cannot
            say. The gap between logged and billed is the interesting part:
            hours that have not reached an invoice are either moving through
            approval or stuck, and only the number says which - so what is
            still in the pipe is named rather than left to be subtracted. */}
        {hoursSummary && (() => {
          const failed = Boolean(hoursSummary.error);
          const missing = Array.isArray(hoursSummary.missing) ? hoursSummary.missing : [];
          // A dash, not "0h", when the figure is not known: the request
          // failed, or the table behind it has not been migrated here yet
          // (the endpoint names those in `missing`). Zero is a claim.
          const show = (v, table) =>
            (failed || missing.includes(table) ? '—' : hrs(v));
          const n = (v) => (failed ? 0 : Number(v) || 0);
          // The period the figures are actually for comes back from the
          // server, so the label cannot say "30 days" over an all-time sum.
          const answered = PERIODS.find((p) => p.days === (hoursSummary.days ?? null));
          const hoursPeriod = periodPhrase(answered ? answered.label : periodLabel);
          // Older servers do not send `ready`; the subtraction is what the
          // tile showed before the server took it over.
          const ready = hoursSummary.ready ?? Math.max(0, n(hoursSummary.approved) - n(hoursSummary.billed));
          return (
          <>
            <StatCard
              title="Hours logged"
              period={hoursPeriod}
              value={show(hoursSummary.logged, 'hours_logs')}
              meta={failed ? 'could not load'
                : n(hoursSummary.awaiting) > 0
                  ? `${hrs(hoursSummary.awaiting)} not signed off`
                  : 'all accounted for'}
              onOpen={() => openHoursDrill('logged', 'Hours logged',
                show(hoursSummary.logged, 'hours_logs'))}
            />
            <StatCard
              title="Hours billed"
              period={hoursPeriod}
              value={show(hoursSummary.billed, 'billing_history')}
              tone="success"
              meta={failed ? 'could not load'
                : n(hoursSummary.billed_amount) > 0
                  ? `${moneyRound(hoursSummary.billed_amount)} invoiced`
                  : 'none invoiced'}
              onOpen={() => openHoursDrill('billed', 'Hours billed',
                show(hoursSummary.billed, 'billing_history'))}
            />
            <StatCard
              title="Ready to bill"
              period={hoursPeriod}
              value={show(ready, 'ticket_approvals')}
              tone="info"
              meta={failed ? 'could not load' : 'signed off, not invoiced'}
              onOpen={() => openHoursDrill('ready', 'Ready to bill',
                show(ready, 'ticket_approvals'))}
            />
            <StatCard
              title="Awaiting sign-off"
              period={hoursPeriod}
              value={show(hoursSummary.awaiting, 'ticket_approvals')}
              tone={n(hoursSummary.awaiting) > 0 ? 'critical' : undefined}
              meta={failed ? 'could not load' : 'locked until decided'}
              onOpen={() => openHoursDrill('awaiting', 'Awaiting sign-off',
                show(hoursSummary.awaiting, 'ticket_approvals'))}
            />
          </>
          );
        })()}
      </div>

      {/* Charts */}
      <div className="dash-grid">
        {/* Status Distribution */}
        <div className="dash-card">
          <h3>Status Distribution</h3>
          {/* Rendered and measured: at 300px tall with an 80px radius the pie
              sat in a third of its card and the rest was empty, which is what
              made this column look padded out next to the bar chart. */}
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={chartData.statusDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={78}
                fill="#8884d8"
                dataKey="value"
                onClick={(slice) => slice && openDrill(`${slice.name} tickets`, byStatus(slice.name))}
              >
                {chartData.statusDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} cursor="pointer" />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Priority Distribution */}
        <div className="dash-card">
          <h3>Priority Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData.priorityDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="value"
                fill="#8884d8"
                cursor="pointer"
                onClick={(bar) => bar && openDrill(`${bar.name} priority`, byPriority(bar.name))}
              >
                {chartData.priorityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} cursor="pointer" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two short bar lists that each used to take a full-width band to
          themselves. Paired, they cost one.

          Both are now drillable, like the pie and the bar chart above them.
          They were the only two numbers on this page that did not open the
          list behind them, which made them the two least useful: "Finance has
          nine open tickets" is where the question starts, not where it ends,
          and the alternative was retyping the department into Advanced
          Search. */}
      <div className="dash-grid">
        {/* Department Breakdown */}
        <div className="dash-card">
        <h3>By Department</h3>
        {chartData.departmentBreakdown.map((dept) => (
          <BarRow
            key={dept.name}
            name={dept.name}
            count={dept.count}
            total={stats.totalTickets}
            onOpen={() => openDrill(`${dept.name} tickets`, byDepartment(dept.name))}
          />
        ))}
      </div>

      {/* Category Breakdown */}
      <div className="dash-card">
        <h3>By Category</h3>
        {chartData.categoryBreakdown.map((cat) => (
          <BarRow
            key={cat.name}
            name={cat.name}
            count={cat.count}
            total={stats.totalTickets}
            tone="info"
            onOpen={() => openDrill(`${cat.name} tickets`, byCategory(cat.name))}
          />
        ))}
      </div>

      </div>

      {/* Recent Tickets */}
      <div className="dash-card">
        <h3>Recent Tickets</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="dash-table">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th>ID</th>
                <th>Title</th>
                <th>Status</th>
                <th>Priority</th>
                {/* Who raised it and who has to sign it off. Until a
                    contractor is assigned the only name on the row was blank,
                    which is precisely the stage - waiting on approval - where
                    you need to know whose ticket it is and who it is sitting
                    with. */}
                <th>Raised by</th>
                <th>Approver</th>
                <th>Contractor</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {chartData.recentTickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className="clickable-row"
                  role="link"
                  tabIndex={0}
                  title={`Open ${ticket.ticket_number}`}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/tickets/${ticket.id}`);
                    }
                  }}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                >
                  <td style={{ color: 'var(--color-primary)', fontWeight: '600' }}>{ticket.ticket_number}</td>
                  <td style={{ color: 'var(--color-text-primary)' }}>{ticket.title}</td>
                  <td>
                    <span
                      className="ticket-status-badge"
                      data-status={String(ticket.status || '').toLowerCase().replace(/\s+/g, '-')}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td>
                    <span
                      className="priority-badge"
                      data-priority={String(ticket.priority || '').toLowerCase()}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="dash-person" title={ticket.requester_email || ''}>
                    <RequesterName ticket={ticket} />
                  </td>
                  {/* An em dash would read as "nobody has to approve this".
                      Not naming one is a state a ticket can be in and the
                      board should say which. */}
                  <td className="dash-person" title={ticket.approver_email || ''}>
                    {ticket.approver_name
                      || <span className="dash-none">none set</span>}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                    {ticket.contractor_name || '—'}
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                    {fmtDate(ticket.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drill && (
        <TicketListModal
          title={drill.title}
          subtitle={drill.subtitle}
          tickets={drill.tickets}
          onClose={() => setDrill(null)}
          loading={Boolean(drill.loading)}
          error={Boolean(drill.error)}
        />
      )}
    </div>
  );
}

export default Dashboard;
